import React, { useState } from 'react';
import type { ChoreSchedule, ScheduleFrequency } from '@/entities/chore';
import { cn } from '@/shared/utils/cn';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

const FREQUENCY_OPTIONS: { value: ScheduleFrequency; label: string }[] = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'specific_days', label: 'Specific Days' },
  { value: 'monthly', label: 'Monthly' },
];

const PERIOD_LABEL: Record<ScheduleFrequency, string> = {
  daily: 'day',
  weekly: 'week',
  specific_days: 'day',
  monthly: 'month',
};

const MAX_TIMES_PER_PERIOD = 20;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
interface ScheduleChoreProps {
  schedule: ChoreSchedule | null;
  onChange: (schedule: ChoreSchedule | null) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
function ScheduleChore({ schedule, onChange }: ScheduleChoreProps): React.ReactElement {
  const [enabled, setEnabled] = useState(schedule !== null);

  const handleToggle = (checked: boolean) => {
    setEnabled(checked);
    if (!checked) {
      onChange(null);
    } else {
      onChange({ frequency: 'daily', days: null, monthDay: null, timesPerPeriod: 1 });
    }
  };

  const handleFrequencyChange = (freq: ScheduleFrequency) => {
    if (!schedule) return;
    onChange({
      frequency: freq,
      days: freq === 'weekly' || freq === 'specific_days' ? (schedule.days ?? []) : null,
      monthDay: freq === 'monthly' ? (schedule.monthDay ?? 1) : null,
      timesPerPeriod: 1, // reset to 1 when frequency changes
    });
  };

  const handleTimesPerPeriodChange = (value: string) => {
    if (!schedule) return;
    const parsed = parseInt(value, 10);
    const clamped = Number.isNaN(parsed) ? 1 : Math.min(MAX_TIMES_PER_PERIOD, Math.max(1, parsed));
    onChange({ ...schedule, timesPerPeriod: clamped });
  };

  const handleDayToggle = (dayIndex: number) => {
    if (!schedule) return;
    const currentDays = schedule.days ?? [];
    const newDays = currentDays.includes(dayIndex)
      ? currentDays.filter((d) => d !== dayIndex)
      : [...currentDays, dayIndex].sort((a, b) => a - b);
    onChange({ ...schedule, days: newDays });
  };

  const handleMonthDayChange = (value: string) => {
    if (!schedule) return;
    const parsed = parseInt(value, 10);
    const clamped = Number.isNaN(parsed) ? 1 : Math.min(31, Math.max(1, parsed));
    onChange({ ...schedule, monthDay: clamped });
  };

  const showDayPicker =
    enabled &&
    schedule &&
    (schedule.frequency === 'weekly' || schedule.frequency === 'specific_days');

  const showMonthDay = enabled && schedule && schedule.frequency === 'monthly';

  return (
    <div className="flex flex-col gap-4">
      {/* Enable toggle */}
      <label className="flex items-center gap-3 cursor-pointer">
        <input
          type="checkbox"
          id="schedule-enabled"
          checked={enabled}
          onChange={(e) => handleToggle(e.target.checked)}
          className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
          aria-label="Enable recurring schedule"
        />
        <span className="text-sm font-medium text-slate-700">Recurring schedule</span>
      </label>

      {enabled && schedule && (
        <div className="ml-7 flex flex-col gap-3 border-l-2 border-slate-100 pl-4">
          {/* Frequency */}
          <div className="flex flex-col gap-1">
            <label htmlFor="schedule-frequency" className="text-sm font-medium text-slate-700">
              Frequency
            </label>
            <select
              id="schedule-frequency"
              value={schedule.frequency}
              onChange={(e) => handleFrequencyChange(e.target.value as ScheduleFrequency)}
              className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {FREQUENCY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Day-of-week picker */}
          {showDayPicker && (
            <div className="flex flex-col gap-1">
              <span className="text-sm font-medium text-slate-700">Days of the week</span>
              <div className="flex flex-wrap gap-2" role="group" aria-label="Select days of the week">
                {DAY_LABELS.map((label, idx) => {
                  const selected = (schedule.days ?? []).includes(idx);
                  return (
                    <button
                      key={label}
                      type="button"
                      aria-pressed={selected}
                      aria-label={label}
                      onClick={() => handleDayToggle(idx)}
                      className={cn(
                        'h-8 w-10 rounded-md text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1',
                        selected
                          ? 'bg-primary-600 text-white'
                          : 'border border-slate-300 bg-white text-slate-600 hover:bg-slate-50',
                      )}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Month day picker */}
          {showMonthDay && (
            <div className="flex flex-col gap-1">
              <label htmlFor="schedule-monthday" className="text-sm font-medium text-slate-700">
                Day of month
              </label>
              <input
                id="schedule-monthday"
                type="number"
                min={1}
                max={31}
                value={schedule.monthDay ?? 1}
                onChange={(e) => handleMonthDayChange(e.target.value)}
                className="block w-24 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                aria-label="Day of month (1-31)"
              />
            </div>
          )}

          {/* Times per period */}
          <div className="flex flex-col gap-1">
            <label htmlFor="schedule-times" className="text-sm font-medium text-slate-700">
              Times per {PERIOD_LABEL[schedule.frequency]}
            </label>
            <input
              id="schedule-times"
              type="number"
              min={1}
              max={MAX_TIMES_PER_PERIOD}
              value={schedule.timesPerPeriod ?? 1}
              onChange={(e) => handleTimesPerPeriodChange(e.target.value)}
              className="block w-24 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
              aria-label={`Number of times per ${PERIOD_LABEL[schedule.frequency]}`}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default ScheduleChore;
